import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Home, Calendar } from "lucide-react";
import axios from "axios";

interface BookingDetails {
  id: number;
  workspaceTitle: string;
  startDate: string;
  endDate: string;
  totalPrice: number;
  status: string;
  paymentStatus: string;
}

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingDetails, setBookingDetails] = useState<BookingDetails | null>(
    null
  );

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        setError("No session ID found");
        setIsVerifying(false);
        return;
      }

      try {
        // Verify the payment with the backend
        const response = await axios.post("/api/payment/verify-payment", {
          sessionId: sessionId,
        });

        if (response.data.success && response.data.isPaymentSuccessful) {
          // Extract booking details
          const booking = response.data.booking;
          if (booking) {
            setBookingDetails({
              id: booking.id,
              workspaceTitle: booking.workspaceTitle,
              startDate: booking.startDate,
              endDate: booking.endDate,
              totalPrice: booking.totalPrice,
              status: booking.status,
              paymentStatus: booking.paymentStatus,
            });
          }
          setIsVerifying(false);
        } else {
          setError(response.data.message || "Payment verification failed");
          setIsVerifying(false);
        }
      } catch (err) {
        console.error("Error verifying payment:", err);
        setError("Failed to verify payment. Please check your bookings.");
        setIsVerifying(false);
      }
    };

    verifyPayment();
  }, [sessionId]);

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-red-600 text-3xl">✕</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            Payment Verification Failed
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full overflow-hidden">
        {/* Success Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 p-8 text-center">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Payment Successful!
          </h1>
          <p className="text-green-100">Your booking has been confirmed</p>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Success Message */}
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-3">
              Thank you for your booking!
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Your payment has been processed successfully and your booking is
              now confirmed. You will receive a confirmation email shortly with
              all the details.
            </p>
          </div>

          {/* Booking Details */}
          {bookingDetails && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-8 border border-blue-200">
              <h3 className="font-semibold text-gray-800 mb-4 text-lg">
                Booking Details
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Booking Reference:</span>
                  <span className="font-semibold text-gray-800">
                    #{bookingDetails.id}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Workspace:</span>
                  <span className="font-semibold text-gray-800">
                    {bookingDetails.workspaceTitle}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Check-in:</span>
                  <span className="font-semibold text-gray-800">
                    {new Date(bookingDetails.startDate).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Check-out:</span>
                  <span className="font-semibold text-gray-800">
                    {new Date(bookingDetails.endDate).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-blue-200">
                  <span className="text-gray-600 font-medium">Total Paid:</span>
                  <span className="font-bold text-green-600 text-lg">
                    ${bookingDetails.totalPrice.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Status:</span>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                    {bookingDetails.status}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Information Cards */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {/* What's Next Card */}
            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                What's Next?
              </h3>
              <ul className="text-sm text-blue-700 space-y-2">
                <li>• Check your email for booking confirmation</li>
                <li>• View booking details in your account</li>
                <li>• The workspace owner will contact you shortly</li>
                <li>• Save the booking reference for your records</li>
              </ul>
            </div>

            {/* Important Info Card */}
            <div className="bg-green-50 rounded-xl p-6">
              <h3 className="font-semibold text-green-800 mb-3 flex items-center">
                <CheckCircle className="w-5 h-5 mr-2" />
                Important Information
              </h3>
              <ul className="text-sm text-green-700 space-y-2">
                <li>• Your payment has been secured</li>
                <li>• Receipt sent to your email</li>
                <li>• Booking is immediately confirmed</li>
                <li>• Contact support if you have questions</li>
              </ul>
            </div>
          </div>

          {/* Session ID (for reference) */}
          {sessionId && (
            <div className="bg-gray-50 rounded-xl p-4 mb-8">
              <p className="text-xs text-gray-500 mb-1">
                Transaction Reference:
              </p>
              <p className="text-sm font-mono text-gray-700 break-all">
                {sessionId}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="grid md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate("/account?mode=travel&section=bookings")}
              className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-md hover:shadow-lg flex items-center justify-center"
            >
              <Calendar className="w-5 h-5 mr-2" />
              View My Bookings
            </button>
            <button
              onClick={() => navigate("/")}
              className="bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center"
            >
              <Home className="w-5 h-5 mr-2" />
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
